import { formatNumberIT } from "@/foundation/formatting";

/**
 * P2.0 STEP C1 · K7 · Split parziale di lib/constants.js.
 *
 * I simboli CORE MONTHS_IT, WEEKDAYS_SHORT, fmtHours, fmtHoursSigned sono
 * stati assimilati in `foundation/formatting/` (Suite-wide, prodotto-agnostici).
 * Vengono qui re-esportati per preservare la retrocompatibilità di
 * `@/lib/constants` verso i 18 consumatori attuali — nessuna modifica funzionale.
 *
 * Le parti SHARED PRODUCT (CAUSE_*, ATTENDANCE_*, hydrateCauseCatalog) restano
 * fisicamente in questo file finché non verrà istanziato il perimetro
 * `packs/gi_presenze/*` (previsto in P2.x successivi, fuori scope P2.0).
 */
export { MONTHS_IT, WEEKDAYS_SHORT, fmtHours, fmtHoursSigned } from "@/foundation/formatting";


export const CAUSE_CODES = [
  "ORDINARIO",
  "STRAORDINARIO",
  "FERIE",
  "PERMESSI",
  "PERMESSI_104",
  "EX_FESTIVITA",
  "PERMESSI_STUDIO",
  "MALATTIA",
  "FESTIVITA",
  "SANTO_PATRONO",
  "CONG_PARENTALE",
  "MATERNITA",
  "ALLATTAMENTO",
  "RECUPERO",
  "ASSENZA_NR",
];

// Colonne sempre visibili nella griglia Presenze, nell'ordine operativo.
// Le altre causali si attivano dal menu verticale e restano visibili quando
// contengono dati, senza trasformare il conguaglio banca ore in ore giornaliere.
export const ATTENDANCE_CORE_CAUSES = [
  "ORDINARIO",
  "FERIE",
  "PERMESSI",
  "EX_FESTIVITA",
  "STRAORDINARIO",
  "MALATTIA",
  "FESTIVITA",
  "SANTO_PATRONO",
  "PERMESSI_104",
  "PERMESSI_STUDIO",
];

export const ATTENDANCE_OPTIONAL_CAUSES = [
  "MATERNITA",
  "ALLATTAMENTO",
  "CONG_PARENTALE",
  "CONGEDO_MATRIMONIALE",
  "PERMESSI_LUTTO",
  "RECUPERO",
  "ASSENZA_NR",
];

export const CAUSE_LABELS = {
  ORDINARIO: "Lavoro ordinario",
  STRAORDINARIO: "Straordinario",
  FERIE: "Ferie",
  PERMESSI: "Permessi",
  PERMESSI_104: "Permessi L.104",
  PERMESSI_LUTTO: "Permesso Lutto",
  EX_FESTIVITA: "Ex festività",
  PERMESSI_STUDIO: "Permessi studio (150 ore)",
  MALATTIA: "Malattia",
  FESTIVITA: "Festività",
  SANTO_PATRONO: "Santo Patrono",
  CONG_PARENTALE: "Cong. Parentale",
  MATERNITA: "Maternità",
  ALLATTAMENTO: "Allattamento",
  RECUPERO: "Recupero",
  ASSENZA_NR: "Assenza non retrib.",
};

export const CAUSE_SHORT = {
  ORDINARIO: "Ord.",
  STRAORDINARIO: "Str.",
  FERIE: "Fer.",
  PERMESSI: "ROL",
  PERMESSI_104: "L104",
  PERMESSI_LUTTO: "PL",
  EX_FESTIVITA: "EXF.",
  PERMESSI_STUDIO: "PStu",
  MALATTIA: "Mal.",
  FESTIVITA: "FS",
  SANTO_PATRONO: "S.Pat.",
  CONG_PARENTALE: "C.Par.",
  MATERNITA: "Mat.",
  ALLATTAMENTO: "All.",
  RECUPERO: "Rec.",
  ASSENZA_NR: "A.NR",
};

// Palette tonalità morbide (bg-slate-50 come base, accento colore per la causale)
export const CAUSE_COLORS = {
  ORDINARIO:      { bg: "bg-slate-50",  text: "text-emerald-700", border: "border-slate-200", dot: "bg-emerald-500" },
  STRAORDINARIO:  { bg: "bg-slate-50",  text: "text-teal-700",    border: "border-slate-200", dot: "bg-teal-500" },
  FERIE:          { bg: "bg-slate-50",  text: "text-slate-700",   border: "border-slate-200", dot: "bg-amber-500" },
  PERMESSI:       { bg: "bg-slate-50",  text: "text-slate-700",   border: "border-slate-200", dot: "bg-amber-400" },
  PERMESSI_104:   { bg: "bg-slate-50",  text: "text-slate-700",   border: "border-slate-200", dot: "bg-violet-500" },
  PERMESSI_LUTTO: { bg: "bg-slate-50",  text: "text-slate-700",   border: "border-slate-200", dot: "bg-rose-500" },
  EX_FESTIVITA:   { bg: "bg-slate-50",  text: "text-slate-700",   border: "border-slate-200", dot: "bg-amber-300" },
  PERMESSI_STUDIO:{ bg: "bg-slate-50",  text: "text-slate-700",   border: "border-slate-200", dot: "bg-indigo-500" },
  MALATTIA:       { bg: "bg-slate-50",  text: "text-slate-700",   border: "border-slate-200", dot: "bg-violet-400" },
  FESTIVITA:      { bg: "bg-slate-50",  text: "text-slate-700",   border: "border-slate-200", dot: "bg-sky-500" },
  SANTO_PATRONO:  { bg: "bg-slate-50",  text: "text-slate-700",   border: "border-slate-200", dot: "bg-sky-400" },
  CONG_PARENTALE: { bg: "bg-slate-50",  text: "text-slate-700",   border: "border-slate-200", dot: "bg-rose-500" },
  MATERNITA:      { bg: "bg-slate-50",  text: "text-slate-700",   border: "border-slate-200", dot: "bg-rose-400" },
  ALLATTAMENTO:   { bg: "bg-slate-50",  text: "text-slate-700",   border: "border-slate-200", dot: "bg-rose-300" },
  RECUPERO:       { bg: "bg-slate-50",  text: "text-slate-700",   border: "border-slate-200", dot: "bg-slate-500" },
  ASSENZA_NR:     { bg: "bg-slate-50",  text: "text-slate-700",   border: "border-slate-200", dot: "bg-red-500" },
};

const DOT_BY_COLOR = {
  slate: "bg-slate-500", emerald: "bg-emerald-500", teal: "bg-teal-500",
  amber: "bg-amber-500", violet: "bg-violet-500", indigo: "bg-indigo-500",
  rose: "bg-rose-500", sky: "bg-sky-500", red: "bg-red-500",
  blue: "bg-blue-500", orange: "bg-orange-500",
};

function causeShort(label, code) {
  const words = String(label || code || "").trim().split(/\s+/).filter(Boolean);
  if (words.length > 1) return words.slice(0, 3).map((w) => w[0]?.toUpperCase()).join(".");
  const value = words[0] || code || "";
  return value.length > 7 ? `${value.slice(0, 6)}.` : value;
}

/** Allinea il catalogo UI alle causali configurate nel database. */
export function hydrateCauseCatalog(causes = []) {
  causes.filter((c) => c?.active !== false && c?.code).forEach((c) => {
    if (!CAUSE_CODES.includes(c.code)) CAUSE_CODES.push(c.code);
    CAUSE_LABELS[c.code] = c.label || c.code;
    if (!CAUSE_SHORT[c.code]) CAUSE_SHORT[c.code] = causeShort(c.label, c.code);
    if (!CAUSE_COLORS[c.code]) {
      CAUSE_COLORS[c.code] = {
        bg: "bg-slate-50",
        text: "text-slate-700",
        border: "border-slate-200",
        dot: DOT_BY_COLOR[c.color] || "bg-slate-500",
      };
    }
  });
  return causes.filter((c) => c?.active !== false && c?.code);
}

export const WEEKDAYS_FULL = ["Lunedì","Martedì","Mercoledì","Giovedì","Venerdì","Sabato","Domenica"];

/**
 * fmtNum — formatter numerico UNICO della Suite.
 * Regola: qualsiasi numero visualizzato SEMPRE 2 decimali con virgola italiana.
 * Esempi:
 *   fmtNum(5)      → "5,00"
 *   fmtNum(4.6667) → "4,67"
 *   fmtNum(null)   → "0,00"
 *   fmtNum(-3)     → "-3,00"
 *   fmtNum("2.5")  → "2,50"
 * Passando `signed=true` prefissa i positivi con "+" (0 non ha segno).
 */
export function fmtNum(n, { signed = false } = {}) {
  if (n === null || n === undefined || n === "" || Number.isNaN(Number(n))) return "0,00";
  const v = Number(n);
  const s = v.toFixed(2).replace(".", ",");
  if (signed && v > 0) return `+${s}`;
  return s;
}

/** Formatter integer con separatore delle migliaia (es. 1.234). Utile per contatori. */
export function fmtInt(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "0";
  return formatNumberIT(n, 0, "0");
}


/** Restituisce {year, month} dell'ultimo mese concluso rispetto ad oggi.
 *  Esempio: oggi 08/07/2026 → {year: 2026, month: 6} */
export function lastCompletedMonth(now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth() + 1; // 1..12
  if (m === 1) return { year: y - 1, month: 12 };
  return { year: y, month: m - 1 };
}

/** Data l'attuale data e il mese di inizio semestre configurato (1..12), calcola
 *  {year, month} di inizio del semestre corrente. length in mesi. */
export function currentSemesterStart(now = new Date(), semesterStartMonth = 1, length = 6) {
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  // trova l'ultimo "inizio semestre" ≤ oggi
  const startMonths = [];
  let cur = semesterStartMonth;
  for (let i = 0; i < Math.ceil(12 / length); i++) {
    startMonths.push(cur);
    cur = ((cur - 1 + length) % 12) + 1;
  }
  startMonths.sort((a, b) => a - b);
  let sy = y, sm = semesterStartMonth;
  const candidates = startMonths.map((sMonth) => ({ year: y, month: sMonth }))
    .concat(startMonths.map((sMonth) => ({ year: y - 1, month: sMonth })));
  // trova la più recente ≤ (y, m)
  candidates.sort((a, b) => (b.year * 12 + b.month) - (a.year * 12 + a.month));
  for (const c of candidates) {
    if (c.year * 12 + c.month <= y * 12 + m) return c;
  }
  return { year: sy, month: sm };
}
